
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Transaction, MonthlySummary, Household, Category } from '../types';
import { getFinancialAudit, FinancialInsight } from '../services/aiService';
import { 
  ArrowUpRight, 
  ArrowDownRight, 
  ChevronRight,
  Users,
  User as UserIcon,
  PieChart as PieIcon,
  TrendingUp,
  ShieldCheck,
  BrainCircuit,
  Zap,
  Sparkles
} from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const [viewMode, setViewMode] = useState<'personal' | 'household'>('personal');
  const [activeHouseholdId, setActiveHouseholdId] = useState<string | null>(null);
  const [households, setHouseholds] = useState<Household[]>([]);
  
  const [summary, setSummary] = useState<MonthlySummary>({ income: 0, expense: 0, debt: 0, receivable: 0 });
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [categoryData, setCategoryData] = useState<{name: string, value: number, color: string}[]>([]);
  const [insights, setInsights] = useState<FinancialInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const navigate = useNavigate();

  const CATEGORY_COLORS: Record<string, string> = {
    [Category.FOOD]: '#fbbf24',
    [Category.TRAVEL]: '#3b82f6',
    [Category.SHOPPING]: '#64748b',
    [Category.BILLS]: '#f43f5e',
    [Category.HEALTH]: '#8b5cf6',
    [Category.EDUCATION]: '#ec4899',
    [Category.ENTERTAINMENT]: '#6366f1',
    [Category.SALARY]: '#10b981',
    [Category.INVESTMENT]: '#0d9488',
    [Category.OTHERS]: '#94a3b8'
  };

  useEffect(() => {
    fetchHouseholds();
  }, []);

  useEffect(() => {
    fetchData();
  }, [viewMode, activeHouseholdId]);

  const fetchHouseholds = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: memberData } = await supabase.from('household_members').select('household_id').eq('user_id', user.id);
    if (memberData && memberData.length > 0) {
      const ids = memberData.map(m => m.household_id);
      const { data } = await supabase.from('households').select('*').in('id', ids);
      if (data) {
        setHouseholds(data);
        if (data.length > 0) setActiveHouseholdId(data[0].id);
      }
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const start = format(startOfMonth(new Date()), 'yyyy-MM-dd');
    const end = format(endOfMonth(new Date()), 'yyyy-MM-dd');

    let query = supabase.from('transactions').select('*').gte('date', start).lte('date', end);

    if (viewMode === 'personal') {
      query = query.is('household_id', null).eq('user_id', user.id);
    } else if (activeHouseholdId) {
      query = query.eq('household_id', activeHouseholdId);
    } else {
      setLoading(false);
      return;
    }

    const { data: txs } = await query.order('date', { ascending: false });

    if (txs) {
      const sums = (txs as Transaction[]).reduce((acc: MonthlySummary, curr: Transaction) => {
        const val = Number(curr.amount);
        if (curr.type === 'income') acc.income += val;
        else if (curr.type === 'expense') acc.expense += val;
        
        // Active Ledger: Only include unsettled debts/receivables in totals
        if (curr.type === 'debt' && !curr.is_settled) acc.debt += val;
        if (curr.type === 'receivable' && !curr.is_settled) acc.receivable += val;
        
        return acc;
      }, { income: 0, expense: 0, debt: 0, receivable: 0 });

      const catMap: Record<string, number> = {};
      txs.forEach(t => {
        if (t.type === 'expense' || t.type === 'debt') {
          catMap[t.category] = (catMap[t.category] || 0) + Number(t.amount);
        }
      });

      const catChart = Object.entries(catMap).map(([name, value]) => ({
        name,
        value,
        color: CATEGORY_COLORS[name] || '#94a3b8'
      })).sort((a,b) => b.value - a.value);

      setSummary(sums);
      setCategoryData(catChart);
      setRecentTransactions(txs as unknown as Transaction[]);
      
      if (txs.length > 0) {
        fetchAiAudit(txs as unknown as Transaction[]);
      }
    }
    setLoading(false);
  };

  const fetchAiAudit = async (txs: Transaction[]) => {
    setAiLoading(true);
    try {
      const result = await getFinancialAudit(txs);
      setInsights(result);
    } catch (e) {
      console.error("AI Audit failed", e);
    } finally {
      setAiLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const renderDonut = () => {
    if (categoryData.length === 0) return null;
    let total = categoryData.reduce((acc, c) => acc + c.value, 0);
    let cumulativePercent = 0;

    return (
      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
        {categoryData.map((cat, i) => {
          const percent = cat.value / total;
          const startX = Math.cos(2 * Math.PI * cumulativePercent);
          const startY = Math.sin(2 * Math.PI * cumulativePercent);
          cumulativePercent += percent;
          const endX = Math.cos(2 * Math.PI * cumulativePercent);
          const endY = Math.sin(2 * Math.PI * cumulativePercent);
          const largeArcFlag = percent > 0.5 ? 1 : 0;
          const pathData = [
            `M ${50 + 40 * startX} ${50 + 40 * startY}`,
            `A 40 40 0 ${largeArcFlag} 1 ${50 + 40 * endX} ${50 + 40 * endY}`
          ].join(' ');
          return (
            <path key={i} d={pathData} fill="none" stroke={cat.color} strokeWidth="12" strokeLinecap="round" />
          );
        })}
        <circle cx="50" cy="50" r="32" fill="white" />
      </svg>
    );
  };

  if (loading) return <div className="flex items-center justify-center pt-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6 pb-20">
      <div className="flex bg-slate-200/50 p-1.5 rounded-3xl border border-slate-200 shadow-inner">
        <button 
          onClick={() => setViewMode('personal')}
          className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center ${viewMode === 'personal' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <UserIcon size={14} className="mr-2" /> Personal
        </button>
        {households.length > 0 && (
          <button 
            onClick={() => setViewMode('household')}
            className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center ${viewMode === 'household' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500 hover:text-slate-800'}`}
          >
            <Users size={14} className="mr-2" /> Groups
          </button>
        )}
      </div>

      <div className="flex justify-between items-start px-1">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 leading-none">
            {viewMode === 'personal' ? 'Personal Summary' : 'Group Summary'}
          </h1>
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.4em] mt-3">
            {format(new Date(), 'MMMM yyyy')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="sapphire-gradient p-8 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group border border-white/5">
          <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:rotate-12 transition-transform duration-500">
             <TrendingUp size={100} />
          </div>
          <p className="text-[10px] opacity-70 font-black uppercase tracking-[0.2em]">Money In</p>
          <p className="text-4xl font-black mt-2 text-amber-400">{formatCurrency(summary.income)}</p>
          <div className="mt-6 inline-flex items-center text-[10px] font-bold bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10">
            <ShieldCheck size={12} className="mr-2 text-blue-400" /> Your Data Is Safe
          </div>
        </div>

        <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col justify-between">
          <div>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">Total Spend</p>
            <p className="text-4xl font-black text-slate-900 mt-2">{formatCurrency(summary.expense)}</p>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="text-[10px] font-black uppercase text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100">Payable: {formatCurrency(summary.debt)}</span>
            <span className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-100">Due: {formatCurrency(summary.receivable)}</span>
          </div>
        </div>
      </div>

      <section className="bg-white p-1 rounded-[3.5rem] border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden">
        <div className="p-8 bg-slate-50/50 rounded-t-[3.5rem] border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center">
            <div className={`p-3 rounded-2xl mr-4 ${aiLoading ? 'animate-pulse-gold bg-amber-400' : 'bg-[#0f172a]'} transition-all`}>
              <BrainCircuit className="text-white w-6 h-6" />
            </div>
            <div>
              <h3 className="font-black text-slate-900 uppercase tracking-widest text-sm">Tips</h3>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em]">Helpful spending insights</p>
            </div>
          </div>
          {aiLoading && <div className="text-[10px] font-black text-amber-500 uppercase tracking-widest flex items-center"><Zap size={12} className="mr-1 animate-bounce" /> Analyzing</div>}
        </div>
        
        <div className="p-8 space-y-6">
          {aiLoading ? (
            <div className="space-y-4">
              <div className="h-20 bg-slate-50 rounded-3xl animate-pulse"></div>
              <div className="h-20 bg-slate-50 rounded-3xl animate-pulse delay-75"></div>
            </div>
          ) : insights.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {insights.map((insight, i) => (
                <div key={i} className="flex items-start gap-4 p-5 rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className={`p-2.5 rounded-xl shrink-0 ${
                    insight.type === 'warning' ? 'bg-red-50 text-red-500' : 
                    insight.type === 'positive' ? 'bg-emerald-50 text-emerald-500' : 
                    'bg-blue-50 text-blue-500'
                  }`}>
                    {insight.type === 'warning' ? <Zap size={18} /> : insight.type === 'positive' ? <Sparkles size={18} /> : <BrainCircuit size={18} />}
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-1">{insight.title}</h4>
                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed">{insight.advice}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 opacity-30">
              <Sparkles size={32} className="mx-auto mb-4 text-slate-300" />
              <p className="text-[10px] font-black uppercase tracking-widest">Add more entries to see tips</p>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white p-8 rounded-[3.5rem] border border-slate-100 shadow-xl shadow-slate-200/50">
        <div className="flex items-center justify-between mb-10">
          <h3 className="font-black text-slate-900 flex items-center uppercase tracking-wider text-sm">
            <PieIcon size={18} className="mr-3 text-amber-500" />
            Spending Areas
          </h3>
          <div className="h-1 w-12 bg-amber-100 rounded-full"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="relative w-48 h-48 mx-auto">
            {categoryData.length > 0 ? (
              <>
                {renderDonut()}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-1">Total</span>
                  <span className="text-lg font-black text-slate-900">{formatCurrency(summary.expense)}</span>
                </div>
              </>
            ) : (
              <div className="w-full h-full rounded-full border-4 border-dashed border-slate-100 flex items-center justify-center text-slate-300 font-bold uppercase text-[10px] tracking-widest text-center px-4">
                No data yet
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            {categoryData.slice(0, 5).map((cat, idx) => (
              <div key={idx} className="flex items-center justify-between group cursor-pointer" onClick={() => navigate('/history')}>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-4 shadow-sm" style={{ backgroundColor: cat.color }}></div>
                  <span className="text-xs font-bold text-slate-600 group-hover:text-slate-900 transition-colors">{cat.name}</span>
                </div>
                <span className="text-xs font-black text-slate-900">{formatCurrency(cat.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="flex justify-between items-center mb-6 px-2">
          <h3 className="text-lg font-black text-slate-900 tracking-tight uppercase tracking-wider">Recent Activity</h3>
          <button onClick={() => navigate('/history')} className="text-slate-500 text-[10px] font-black uppercase tracking-widest flex items-center bg-slate-100 px-4 py-2 rounded-2xl hover:bg-slate-200 transition-all active:scale-95">
            View All <ChevronRight size={14} className="ml-1" />
          </button>
        </div>
        <div className="space-y-4">
          {recentTransactions.slice(0, 5).map((tx) => (
            <div key={tx.id} onClick={() => navigate(`/edit/${tx.id}`)} className="bg-white p-6 rounded-[2.5rem] flex items-center justify-between border border-slate-100 shadow-sm hover:shadow-md transition-all active:scale-[0.98] cursor-pointer group">
              <div className="flex items-center">
                <div className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center mr-5 shadow-inner transition-transform group-hover:scale-110 ${tx.type === 'expense' || tx.type === 'debt' ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                  {tx.type === 'expense' || tx.type === 'debt' ? <ArrowDownRight size={24} /> : <ArrowUpRight size={24} />}
                </div>
                <div>
                  <p className="font-black text-slate-900 text-sm leading-tight">{tx.category}</p>
                  <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">
                    {format(new Date(tx.date), 'dd MMM')} • <span className={tx.type === 'debt' ? 'text-orange-500' : ''}>{tx.type === 'debt' ? 'Borrowed' : tx.type === 'receivable' ? 'Lent' : tx.type}</span>
                    {tx.is_settled && <span className="ml-2 text-emerald-500 font-black tracking-tight">✓ Settled</span>}
                  </p>
                </div>
              </div>
              <p className={`font-black text-lg ${tx.type === 'expense' || tx.type === 'debt' ? 'text-red-600' : 'text-emerald-700'}`}>
                {formatCurrency(tx.amount)}
              </p>
            </div>
          ))}
          {recentTransactions.length === 0 && (
            <div className="text-center py-10 bg-slate-50 rounded-[2.5rem] border border-dashed border-slate-200">
              <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">No recent transactions</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
